import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";

export default function AdminModules() {
  const [modules, setModules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", course_id: "", order: 0 });

  const load = useCallback(async () => {
    const [m, c] = await Promise.all([api.get("/modules"), api.get("/courses")]);
    setModules(m.data);
    setCourses(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ title: "", description: "", course_id: courses[0]?.id || "", order: modules.length });
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    try {
      await api.post("/modules", form);
      toast.success("Module created");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this module?")) return;
    try {
      await api.delete(`/modules/${id}`);
      toast.success("Module deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const getCourseName = (id) => courses.find((c) => c.id === id)?.title || "Unknown";

  return (
    <AdminLayout>
      <div data-testid="admin-modules" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Modules</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Create and manage learning content</p>
          </div>
          <Button data-testid="create-module-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> New Module
          </Button>
        </div>

        <div className="grid gap-4">
          {modules.map((m, i) => (
            <div key={m.id} data-testid={`module-card-${m.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6 studio-card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-[#C6A969] font-medium">Module {i + 1}</span>
                    <span className="text-xs text-[#2B2B2B]/30">&middot;</span>
                    <span className="text-xs text-[#2B2B2B]/40">{getCourseName(m.course_id)}</span>
                  </div>
                  <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{m.title}</h3>
                  {m.description && <p className="text-[#2B2B2B]/50 text-sm mt-1 line-clamp-2">{m.description}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#2B2B2B]/40">
                    {m.materials?.length > 0 && <span>{m.materials.length} materials</span>}
                    {m.images?.length > 0 && <span>{m.images.length} images</span>}
                    {m.syllabus_path && <span>Syllabus attached</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Link to={`/admin/modules/${m.id}`}>
                    <Button data-testid={`edit-module-${m.id}`} variant="ghost" size="sm" className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                  <Button data-testid={`delete-module-${m.id}`} variant="ghost" size="sm" onClick={() => handleDelete(m.id)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {modules.length === 0 && (
            <div className="text-center py-12">
              <Layers className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
              <p className="text-[#2B2B2B]/40 text-sm">No modules yet. Create your first module.</p>
            </div>
          )}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">New Module</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Create a new learning module</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
                <Input data-testid="module-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
                <Input data-testid="module-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="module-course-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-module-button" onClick={handleCreate} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Create Module
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
